import { Role } from '@/models/common/Role';
import { ManagedUserDtoIn } from '@/models/in/ManagedUserDtoIn';

/** The backend stores "never happened" timestamps as 2000-01-01 rather than leaving them empty. */
const NEVER_TIMESTAMP_PREFIX = '2000-01-01';

/** Roles an admin can grant or revoke from the users screen. */
export const ASSIGNABLE_ROLES = [Role.Admin, Role.User, Role.Debugger] as const;

/** Pseudo role used by the filters to single out accounts that carry no role at all. */
export const NO_ROLE_FILTER_ID = 'none';

export type ActivityFilter = 'all' | 'active30' | 'active90' | 'never';

export const parseUserDate = (value?: string): Date | null => {
  if (!value || value.startsWith(NEVER_TIMESTAMP_PREFIX)) return null;
  // Timestamps arrive in the "u" format ("2025-08-25 09:12:44Z"), which needs the T to parse everywhere.
  const parsed = new Date(value.replace(' ', 'T'));
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const dateTimeFormatter = new Intl.DateTimeFormat('he-IL', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

/** Formatted timestamp, or null when the event never happened. */
export const formatUserDate = (value?: string): string | null => {
  const date = parseUserDate(value);
  return date ? dateTimeFormatter.format(date) : null;
};

/** Sort value for a timestamp column - "never" sorts before every real date. */
export const userDateSortValue = (value?: string): number => parseUserDate(value)?.getTime() ?? 0;

export const getUserFullName = (user: ManagedUserDtoIn): string => [user.firstName, user.lastName].filter(Boolean).join(' ').trim();

export const hasRole = (user: ManagedUserDtoIn, role: Role): boolean => (user.roles ?? []).includes(role);

/** Admin first, then Debugger, then User, and finally accounts with no role at all. */
export const roleSortValue = (user: ManagedUserDtoIn): number => {
  if (hasRole(user, Role.Admin)) return 3;
  if (hasRole(user, Role.Debugger)) return 2;
  if (hasRole(user, Role.User)) return 1;
  return 0;
};

const daysSince = (value?: string): number | null => {
  const date = parseUserDate(value);
  return date ? (Date.now() - date.getTime()) / (1000 * 60 * 60 * 24) : null;
};

export const matchesActivityFilter = (user: ManagedUserDtoIn, filter: ActivityFilter): boolean => {
  if (filter === 'all') return true;

  const days = daysSince(user.lastSuccessfulLoginTime);
  if (filter === 'never') return days === null;
  if (days === null) return false;

  return filter === 'active30' ? days <= 30 : days <= 90;
};

/**
 * Free text search over everything shown for a user - name, email, phone and fund codes -
 * so a single box covers all the parameters the screen displays.
 */
export const matchesSearch = (user: ManagedUserDtoIn, query: string): boolean => {
  const trimmed = query.trim().toLowerCase();
  if (!trimmed) return true;

  const haystack = [getUserFullName(user), user.email, user.phoneNumber, (user.fundIds ?? []).join(' '), (user.roles ?? []).join(' ')]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  // Every word has to appear somewhere, which makes "כהן 1234" find a Cohen in fund 1234.
  return trimmed.split(/\s+/).every((word) => haystack.includes(word));
};
